// Focal Tooltip Web Component
class FocalTooltip extends HTMLElement {
  constructor() {
    super();
    this.isVisible = false;
    this.init();
  }

  init() {
    this.createTooltipContent();
    this.bindEvents();
  }

  createTooltipContent() {
    // The content is already provided in the template
    // We just need to ensure it's properly structured
    this.tooltipContent = this.querySelector('.focal-tooltip__content');
    if (!this.tooltipContent) {
      this.tooltipContent = document.createElement('div');
      this.tooltipContent.className = 'focal-tooltip__content';
      this.appendChild(this.tooltipContent);
    }
  }

  bindEvents() {
    // Listen for show/hide events
    this.addEventListener('show-tooltip', () => this.show());
    this.addEventListener('hide-tooltip', () => this.hide());
  }

  show() {
    if (!this.isVisible) {
      this.isVisible = true;
      this.classList.add('active');
      this.calculatePosition();
    }
  }

  hide() {
    if (this.isVisible) {
      this.isVisible = false;
      this.classList.remove('active');
    }
  }

  calculatePosition() {
    const focalPoint = this.getFocalPoint();
    if (!focalPoint) return;

    const productSpot = this.closest('.product-spot');
    if (!productSpot) return;

    // Get the percentage positions from the focal point
    const focalPointLeft = focalPoint.style.left; // e.g., "25%"
    const focalPointTop = focalPoint.style.top;   // e.g., "50%"

    // Convert percentage to decimal
    const leftPercent = parseFloat(focalPointLeft) / 100;
    const topPercent = parseFloat(focalPointTop) / 100;

    // Get the product-spot container dimensions
    const productSpotRect = productSpot.getBoundingClientRect();

    // Temporarily make tooltip visible to get accurate dimensions
    const wasVisible = this.isVisible;
    if (!wasVisible) {
      this.style.visibility = 'hidden';
      this.style.display = 'block';
      this.style.position = 'absolute';
    }

    const tooltipRect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Restore original visibility if it was hidden
    if (!wasVisible) {
      this.style.visibility = '';
      this.style.display = '';
      this.style.position = '';
    }

    // Offset from focal point
    const offset = 25; // 25px offset from focal point

    // Calculate horizontal position (center tooltip on focal point)
    let left = (productSpotRect.width * leftPercent) - (tooltipRect.width / 2);

    // Adjust horizontal position if tooltip goes outside product-spot
    if (left < 0) {
      left = 0; // Align to left edge
    } else if (left + tooltipRect.width > productSpotRect.width) {
      left = productSpotRect.width - tooltipRect.width; // Align to right edge
    }

    // Calculate vertical position with viewport detection
    const focalPointViewportTop = productSpotRect.top + (productSpotRect.height * topPercent);
    const tooltipHeight = tooltipRect.height;

    // Try to position above the focal point first
    let top = (productSpotRect.height * topPercent) - tooltipHeight - offset;
    let arrowPosition = 'top'; // Default arrow position

    // Check if tooltip would overflow the viewport at the top
    const tooltipViewportTop = focalPointViewportTop - tooltipHeight - offset;
    const wouldOverflowTop = tooltipViewportTop < 10; // 10px margin from viewport top

    // Check if tooltip would overflow the viewport at the bottom
    const tooltipViewportBottom = focalPointViewportTop + offset;
    const wouldOverflowBottom = tooltipViewportBottom + tooltipHeight > viewportHeight - 10; // 10px margin from viewport bottom

    // Determine optimal position
    if (wouldOverflowTop && !wouldOverflowBottom) {
      // Can't fit above, but can fit below
      top = (productSpotRect.height * topPercent) + offset;
      arrowPosition = 'bottom';
    } else if (wouldOverflowTop && wouldOverflowBottom) {
      // Can't fit above or below, position at top of product-spot
      top = 0;
      arrowPosition = 'top';
    } else {
      // Can fit above (default)
      top = (productSpotRect.height * topPercent) - tooltipHeight - offset;
      arrowPosition = 'top';
    }

    // Ensure tooltip doesn't go outside product-spot boundaries
    if (top < 0) {
      top = 0;
      arrowPosition = 'top';
    } else if (top + tooltipHeight > productSpotRect.height) {
      top = productSpotRect.height - tooltipHeight;
      arrowPosition = 'bottom';
    }

    // Apply the calculated position
    this.style.left = left + 'px';
    this.style.top = top + 'px';

    // Update arrow position
    this.updateArrowPosition(arrowPosition);
  }

  updateArrowPosition(position) {
    const tooltipContent = this.querySelector('.focal-tooltip__content');
    if (!tooltipContent) return;

    // Remove all arrow classes
    tooltipContent.classList.remove('arrow-top', 'arrow-bottom', 'arrow-left', 'arrow-right');

    // Add the appropriate arrow class
    tooltipContent.classList.add(`arrow-${position}`);
  }

  getFocalPoint() {
    const focalPointIndex = this.className.match(/focal-tooltip--(\d+)/);
    if (focalPointIndex) {
      const productSpot = this.closest('.product-spot');
      return productSpot?.querySelector(`.focal-point--${focalPointIndex[1]}`);
    }
    return null;
  }

  // Calculate position when component mounts and handle resize
  connectedCallback() {
    // Calculate initial position after a brief delay to ensure DOM is ready
    setTimeout(() => {
      this.calculatePosition();
    }, 0);

    // Update position on window resize
    window.addEventListener('resize', () => {
      this.calculatePosition();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.calculatePosition);
  }
}

// Register the web component
customElements.define('focal-tooltip', FocalTooltip);

// Focal Tooltip Handler
class FocalTooltipHandler {
  constructor() {
    this.isMobile = window.innerWidth <= 768;
    this.hoverTimeout = null;
    this.activeTooltip = null;
    this.init();
  }

  init() {
    this.bindEvents();
    this.handleResize();
  }

  bindEvents() {
    // Handle focal point interactions
    document.addEventListener('mouseenter', (e) => {
      if (e.target instanceof Element && e.target.closest('.focal-point') && !this.isMobile) {
        this.handleFocalPointHover(e);
      }
    });

    document.addEventListener('mouseleave', (e) => {
      if (e.target instanceof Element && e.target.closest('.focal-point') && !this.isMobile) {
        this.handleFocalPointLeave(e);
      }
    });

    document.addEventListener('focusin', (e) => {
      if (e.target instanceof Element && e.target.closest('.focal-point') && !this.isMobile) {
        this.handleFocalPointFocus(e);
      }
    });

    document.addEventListener('focusout', (e) => {
      if (e.target instanceof Element && e.target.closest('.focal-point') && !this.isMobile) {
        this.handleFocalPointBlur(e);
      }
    });

    // Handle keyboard events on focal points
    document.addEventListener('keydown', (e) => {
      if (e.target instanceof Element && e.target.closest('.focal-point') && !this.isMobile) {
        this.handleFocalPointKeydown(e);
      }
    });

    // Handle focus events on tooltip content
    document.addEventListener('focusin', (e) => {
      if (e.target instanceof Element && e.target.closest('focal-tooltip')) {
        this.handleTooltipFocus(e);
      }
    });

    // Handle focus leaving tooltip content
    document.addEventListener('focusout', (e) => {
      if (e.target instanceof Element && e.target.closest('focal-tooltip')) {
        this.handleTooltipBlur(e);
      }
    });

    document.addEventListener('click', (e) => {
      if (e.target instanceof Element && e.target.closest('.focal-point')) {
        e.preventDefault();
        e.stopPropagation();
        this.handleFocalPointClick(e);
      } else {
        // Hide tooltip when clicking outside
        this.hideTooltip();
      }
    });

    // Handle window resize
    window.addEventListener('resize', () => {
      this.handleResize();
    });

    // Handle scroll events to update tooltip positions
    window.addEventListener('scroll', () => {
      this.handleScroll();
    }, { passive: true });
  }

  handleResize() {
    this.isMobile = window.innerWidth <= 768;
  }

  handleScroll() {
    // Update position of active tooltip if it exists
    if (this.activeTooltip && this.activeTooltip.isVisible) {
      // Force recalculation of position and arrow
      this.activeTooltip.calculatePosition();
    }
  }

  handleFocalPointClick(e) {
    const focalPoint = e.target instanceof Element ? e.target.closest('.focal-point') : null;
    if (focalPoint) {
      // Toggle tooltip on mobile - show if not visible, hide if visible
      const productSpot = focalPoint instanceof Element ? focalPoint.closest('.product-spot') : null;
      const focalPointIndex = focalPoint.className.match(/focal-point--(\d+)/);

      if (productSpot && focalPointIndex) {
        const tooltip = productSpot.querySelector(`focal-tooltip.focal-tooltip--${focalPointIndex[1]}`);
        if (tooltip) {
          if (tooltip.isVisible) {
            this.hideTooltip();
          } else {
            this.showTooltip(focalPoint);
          }
        }
      }
    }
  }

  handleFocalPointHover(e) {
    const focalPoint = e.target instanceof Element ? e.target.closest('.focal-point') : null;
    if (focalPoint) {
      this.hoverTimeout = setTimeout(() => {
        this.showTooltip(focalPoint);
      }, 300);
    }
  }

  handleFocalPointLeave(e) {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    setTimeout(() => {
      if (!this.isTooltipHovered()) {
        this.hideTooltip();
      }
    }, 100);
  }

  handleFocalPointKeydown(e) {
    const focalPoint = e.target instanceof Element ? e.target.closest('.focal-point') : null;
    if (focalPoint && e.key === 'Enter') {
      e.preventDefault();
      this.showTooltip(focalPoint);

      // Focus the product link in the tooltip
      setTimeout(() => {
        const tooltip = this.activeTooltip;
        if (tooltip) {
          const productLink = tooltip.querySelector('.focal-tooltip__button');
          if (productLink) {
            productLink.focus();
          }
        }
      }, 100);
    }
  }

  handleFocalPointFocus(e) {
    const focalPoint = e.target instanceof Element ? e.target.closest('.focal-point') : null;
    if (focalPoint) {
      // Show tooltip on hover for desktop
      this.hoverTimeout = setTimeout(() => {
        this.showTooltip(focalPoint);
      }, 300);
    }
  }

  handleFocalPointBlur(e) {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    // Only hide tooltip if focus is not moving to the tooltip content
    setTimeout(() => {
      if (!this.isTooltipFocused()) {
        this.hideTooltip();
      }
    }, 100);
  }

  isTooltipHovered() {
    return this.activeTooltip && this.activeTooltip.matches(':hover');
  }

  handleTooltipFocus(e) {
    // When focus moves to tooltip content, keep it open
    // This prevents the tooltip from closing when tabbing to the product link
  }

  handleTooltipBlur(e) {
    // When focus leaves tooltip content, close it after a short delay
    // This allows for focus to move between elements within the tooltip
    setTimeout(() => {
      if (!this.isTooltipFocused()) {
        this.hideTooltip();
      }
    }, 100);
  }

  isTooltipFocused() {
    return this.activeTooltip && this.activeTooltip.contains(document.activeElement);
  }

  showTooltip(focalPoint) {
    // Hide any existing tooltips
    this.hideTooltip();

    // Find the corresponding tooltip
    const productSpot = focalPoint instanceof Element ? focalPoint.closest('.product-spot') : null;
    const focalPointIndex = focalPoint.className.match(/focal-point--(\d+)/);

    if (productSpot && focalPointIndex) {
      const tooltip = productSpot.querySelector(`focal-tooltip.focal-tooltip--${focalPointIndex[1]}`);
      if (tooltip) {
        this.activeTooltip = tooltip;
        tooltip.dispatchEvent(new CustomEvent('show-tooltip'));
      }
    }
  }

  hideTooltip() {
    if (this.activeTooltip) {
      this.activeTooltip.dispatchEvent(new CustomEvent('hide-tooltip'));
      this.activeTooltip = null;
    }
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }
}

// Initialize tooltip handler when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new FocalTooltipHandler();
});

// Handle Shopify section loads
document.addEventListener('shopify:section:load', () => {
  new FocalTooltipHandler();
}); 